import Link, { LINKS, LinkStateProps, LinkType } from "@components/Link/Link";
import { Button as AntdButton } from "antd";
import cn from "classnames";
import React, { CSSProperties, useState } from "react";
import * as styles from "./Button.module.scss";
// import SVG from "react-inlinesvg";
import { ArrowRightIcons } from "@assets/icons";
import ButtonV2 from "@components/ButtonV2/ButtonV2";
import Input from "@components/Input/Input";
import TypographyV2 from "@components/TypographyV2/TypographyV2";
import { Colors } from "@helpers/Colors";
import { Arrow } from "../../pages/ai-product/_components/_AI_Path/_AI_Path";
import classNames from "classnames";
import { StaticImage } from "gatsby-plugin-image";
import { Typography } from "..";

export type ButtonProps = {
	className?: string;
	/** type of button */
	type?:
		| "primary"
		| "ghost"
		| "link"
		| "plus"
		| "v2primary"
		| "v2secondary"
		| "v2link"
		| "v2ghost"
		| "v2secondary_jti";
	disabled?: boolean;
	style?: React.CSSProperties;
	children?: any;
	onClick?: (e?: React.MouseEvent) => void;
	link?: LinkType;
	to?: string;
	target?: string;
	internal?: boolean;
	state?: LinkStateProps;
	newLayout?: boolean;
	version?: "v2";
	inHeader?: boolean;
	textColor?: Colors;
};

/**
 * The Link Button component, (Default: signUp button )
 * if no {onClick} or {to} props : act as signUp button
 * if {onClick} props: act as normal button
 * if {to} props and no {target}: act as interanl Link button (Gatsb link)
 * if {to} props and {target}: act as external link button
 * */
export default function Button({
	className,
	type = "primary",
	disabled,
	onClick,
	children,
	style,
	link,
	target,
	to,
	internal,
	state,
	version,
	inHeader,
	textColor,
	newLayout = false,
}: ButtonProps) {
	let TypographyComponent = (
		<Typography variant="BUTTON" newLayout={newLayout}>
			{children}
		</Typography>
	);

	if (version === "v2") {
		const variant = inHeader ? "BODY_TEXT_MEDIUM" : "BODY_TEXT_LARGE";
		const color = textColor === "white" ? "gray-100" : "brand-500";
		TypographyComponent = (
			<TypographyV2
				variant={variant}
				weight={"REGULAR"}
				color={color}
				className={styles.overrideMargin}
			>
				{children}
			</TypographyV2>
		);
	}

	let ButtonComponent = (
		<AntdButton
			shape="round"
			className={cn(styles.genemodButton, className, {
				[styles.genemodButton__primary]: type === "primary",
				[styles.genemodButton__v2primary]: type === "v2primary",
				[styles.genemodButton__v2secondary]: type === "v2secondary",
				[styles.genemodButton__v2secondary_jti]:
					type === "v2secondary_jti",
				[styles.genemodButton__v2link]: type === "v2link",
				[styles.genemodButton__v2ghost]: type === "v2ghost",
				[styles.genemodButton__ghost]: type === "ghost",
				// [styles.genemodButton__stretchInMobile]: version === "v2" && !inHeader,
				[styles.genemodButton__v2]: version === "v2",
			})}
			disabled={disabled}
			onClick={onClick}
			style={style}
		>
			{TypographyComponent}
		</AntdButton>
	);

	if (type === "link") {
		ButtonComponent = (
			<div onClick={onClick}>
				<Typography
					variant="BUTTON"
					className={cn(styles.genemodLinkButton, className)}
					style={style}
					newLayout={newLayout}
				>
					{" "}
					{children}{" "}
				</Typography>
			</div>
		);
	}

	if (type === "plus") {
		return (
			<StaticImage
				loading="eager"
				src="./Plus-btn.svg"
				alt="plus icon"
				className={cn(styles.plusButton, className)}
				style={style}
			/>
		);
	}

	if (onClick) {
		return ButtonComponent;
	}

	return (
		<Link
			link={to ? undefined : link || "DEMO"}
			target={target}
			state={state}
			to={to}
			internal={internal}
			className={cn({
				[styles.linkButtonContainer]: version === "v2" && !inHeader,
			})}
		>
			{ButtonComponent}
		</Link>
	);
}

type ArrowLinkButtonProps = Omit<ButtonProps, "type"> & {
	color?: "marine" | "medium_pink" | "ui_01" | "brand";
	containerClass?: string;
	containerStyle?: React.CSSProperties;
};
/** link type button with right-arrow icon */
export function ArrowLinkButton(props: ArrowLinkButtonProps) {
	return (
		<div
			className={cn(
				styles.ArrowLinkButtonContainer,
				props.containerClass
			)}
			style={props.containerStyle}
		>
			{ArrowRightIcons(props.color || "marine")}
			<Button
				type="link"
				{...props}
				className={cn(styles.linkButton, props.className, {
					[styles.linkButton__marine]: props.color === "marine",
					[styles.linkButton__white]: props.color === "ui_01",
				})}
			>
				{props.children}
			</Button>
		</div>
	);
}

type LoginButtonProps = {
	/** type of login button (defualt = "link") */
	type?: "link" | "ghost" | "v2secondary";
	style?: React.CSSProperties;
};
export function LoginButton({ type = "link", style }: LoginButtonProps) {
	return (
		<Button
			newLayout
			style={style}
			type={type}
			link="LOGIN"
			target="_blank"
			textColor="marine"
			version="v2"
		>
			{LINKS.LOGIN.title}
		</Button>
	);
}

export function SignUpButton({ newLayout }: { newLayout?: boolean }) {
	return (
		<Button type="primary" link="SIGN_UP" newLayout={newLayout}>
			{LINKS.SIGN_UP.title}
		</Button>
	);
}

export function SignUpButtonV2({ className,text }: { className?: string ,text?:string}) {
	return (
		<Link link="SIGN_UP" className={styles.linkButtonContainer}>
			<ButtonV2 variant="secondary" mobileFullWidth className={className}>
			{text || LINKS.SIGN_UP.title}
			</ButtonV2>
		</Link>
	);
}

export function GhostSignUpButtonV2({title}:{title:string}) {
	return (
		<Button type="v2ghost" link="SIGN_UP" version="v2" textColor="white">
			{title || LINKS.SIGN_UP.title}
		</Button>
	);
}

type RequestDemoButtonV2Props = {
	mobileFullWidth?: boolean;
	tabletFullWidth?: boolean;
	linkClassName?: string;
	variant?: "primary" | "secondary" | "link" | "v2";
	className?:string;
	text?:string;
};
export function RequestDemoButtonV2({
	mobileFullWidth = true,
	linkClassName,
	variant,
	tabletFullWidth = true,
	className,
	text
}: RequestDemoButtonV2Props) {
	return (
		<Link
			link="DEMO"
			className={classNames(styles.linkButtonContainer, linkClassName)}
		>
			<ButtonV2
				mobileFullWidth={mobileFullWidth}
				tabletFullWidth={tabletFullWidth}
				variant={variant}
				className={className??""}
			>
				{text || LINKS.DEMO_V2.title}
			</ButtonV2>
		</Link>
	);
}

export function HeaderRequestDemoButtonV2() {
	return (
		<Button
			type="v2primary"
			link="DEMO"
			inHeader
			version="v2"
			textColor="white"
		>
			{LINKS.DEMO_V2.title}
		</Button>
	);
}

// Has a different width, border, bg and used in the JoinTI section
export function JoinTIRequestDemoButtonV2({title}:{title:string}) {
	return (
		<Button
			type="v2secondary_jti"
			link="DEMO"
			version="v2"
			textColor="marine"
		>
			{title || LINKS.DEMO_V2.title}
		</Button>
	);
}

type _ButtonProps = {
	style?: CSSProperties;
	className?: string;
	textClassName?: string;
};

export function AIYouWontGetButton() {
	return (
		<div className={styles.v2_demoAIYouWontGet}>
			<Button link="DEMO" type="v2link" version="v2" textColor="gray-700">
				<span className={styles.v2_withIcon}>
					{LINKS.DEMO_V2.title}
					{ArrowRightIcons("marine", "arrow-narrow-right")}
				</span>
			</Button>
		</div>
	);
}

export function LogInButtonV2() {
	return (
		<Link link="LOGIN" className={styles.linkButtonContainer}>
			<ButtonV2 variant="secondary" tabletFullWidth>
				{LINKS.LOGIN.title}
			</ButtonV2>
		</Link>
	);
}

type LearnMoreButtonV2Props = {
	to?: LinkType;
	className?: string;
	textClassName?: string;
	showArrow?: boolean;
	children?: React.ReactNode;
	text?: string;
	arrowColor?:
		| "medium_pink"
		| "marine"
		| "ui_01"
		| "brand"
		| "primary"
		| "brand-primary"
		| "brand-primary-2";
	arrowType?: "arrow-right" | "arrow-narrow-right";
};

export function LearnMoreButtonV2({ to }: LearnMoreButtonV2Props) {
	return (
		<Link to={LINKS[to || "DEMO"].to}>
			<div className={cn(styles.v2_moreButton, styles.v2_learnMore)}>
				<TypographyV2
					weight="MEDIUM"
					style={{ color: "var(--marine-hover)" }}
				>
					Learn More
				</TypographyV2>
				{ArrowRightIcons("marine", "arrow-narrow-right")}
			</div>
		</Link>
	);
}
type LearnMoreButtonDefaultV2Props = {
	to?: LinkType;
	className?: string;
	textClassName?: string;
};

export function LearnButtonDefaultV2({
	to,
	className,
}: LearnMoreButtonDefaultV2Props) {
	return (
		<Link
			to={LINKS[to || "DEMO"].to}
			className={styles.linkButtonContainer}
		>
			<ButtonV2 variant="secondary" className={className}>
				Learn more
			</ButtonV2>
		</Link>
	);
}
export function LearnMoreButtonV3({
	to,
	className,
	textClassName,
	showArrow,
	text,
	children,
	arrowColor = "marine",
	arrowType = "arrow-narrow-right",
}: LearnMoreButtonV2Props) {
	return (
		<Link to={LINKS[to || "DEMO"].to}>
			<div className={cn(styles.v3_learnMore, className)}>
				<TypographyV2
					weight="MEDIUM"
					style={{ color: "var(--marine-hover)" }}
					className={textClassName}
				>
					{children || text || "Learn More"}
				</TypographyV2>
				{(showArrow || showArrow == undefined) &&
					ArrowRightIcons(arrowColor, arrowType)}
			</div>
		</Link>
	);
}

export function LearnMoreButtonV4({
	to,
	className,
	textClassName,
	showArrow,
	children,
}: LearnMoreButtonV2Props) {
	return (
		<Link to={LINKS[to || "DEMO"].to}>
			<div className={cn(styles.v4_learnMore, className)}>
				<TypographyV2
					weight="MEDIUM"
					style={{ color: "var(--marine-hover)" }}
					className={textClassName}
				>
					{children}
				</TypographyV2>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="18"
					height="18"
					viewBox="0 0 18 18"
					fill="none"
				>
					<path
						d="M0.833313 9.00004H17.1666M17.1666 9.00004L8.99998 0.833374M17.1666 9.00004L8.99998 17.1667"
						stroke="#051D85"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
				{/* {(showArrow || showArrow == undefined) &&
					ArrowRightIcons("marine", "arrow-narrow-right")} */}
			</div>
		</Link>
	);
}

export function ReadMoreButtonV2(props: _ButtonProps) {
	return (
		<Link to={LINKS["BLOG"].to}>
			<div className={styles.v2_readMore_container} {...props}>
				<div className={cn(styles.v2_moreButton, styles.v2_readMore)}>
					<TypographyV2 weight="MEDIUM" color="text-tertiary">
						Read more blogs
					</TypographyV2>
					{ArrowRightIcons("marine", "arrow-narrow-right")}
				</div>
			</div>
		</Link>
	);
}
export function ReadMoreButtonV3(props: _ButtonProps) {
	return (
		<Link to={LINKS["BLOG"].to}>
			<div className={styles.v2_readMore_container} style={props.style}>
				<div
					className={cn(
						styles.v2_moreButton,
						styles.v2_readMore,
						props.className
					)}
				>
					<TypographyV2
						weight="MEDIUM"
						color="text-tertiary"
						className={props.textClassName}
					>
						Read more blogs
					</TypographyV2>
					{ArrowRightIcons("primary", "arrow-right")}
				</div>
			</div>
		</Link>
	);
}

export function LearnMoreButton() {
	return <Button link="DEMO">Learn more</Button>;
}

export function GetStartedButton() {
	return (
		<Button link="SIGN_UP" target="_blank">
			Get started
		</Button>
	);
}

export function CreateFreeAccountButton(props: ArrowLinkButtonProps) {
	return (
		<ArrowLinkButton {...props} link="SIGN_UP" target="_blank">
			Or create a free account
		</ArrowLinkButton>
	);
}
type DemoButtonWithInputProps = {
	lightmode?: boolean;
	wideInput?: boolean;
	showSignUp?: boolean;
	showSignUpFreeButton?: boolean;
	label?: string;
	placeholder?: string;
	mobileFullWidth?: boolean;
	emailInputClass?: string;
	emailInputLabelClass?: string;
	emailInputWrapperClass?: string;
	buttonLinkClass?: string;
	inputWrapperOverwrite?: string;
};

export function DemoButtonWithInput({
	lightmode,
	wideInput = false,
	showSignUp = false,
	showSignUpFreeButton = false,
	label,
	placeholder,
	mobileFullWidth = true,
	emailInputClass,
	emailInputLabelClass,
	emailInputWrapperClass,
	inputWrapperOverwrite,
	buttonLinkClass,
}: DemoButtonWithInputProps) {
	const [workEmail, setWorkEmail] = useState("");
	return (
		<div className={styles.DemoButtonWithInputContainer}>
			{!showSignUp && (
				<Input
					inputClassName={cn(styles.emailInput, emailInputClass, {
						[styles.wide]: wideInput,
					})}
					lightmode={lightmode}
					label={label || "Your work email"}
					placeholder={placeholder}
					value={workEmail}
					onChange={setWorkEmail}
					inputLabelClass={emailInputLabelClass}
					className={emailInputWrapperClass}
					inputWrapperOverwrite={inputWrapperOverwrite}
				/>
			)}
			<Link to={LINKS.DEMO.to} className={buttonLinkClass}>
				<ButtonV2
					mobileFullWidth={mobileFullWidth}
					className={
						showSignUp
							? styles.genemodButton__v2primary
							: styles.button
					}
				>
					Request demo
				</ButtonV2>
			</Link>
			{showSignUpFreeButton && (
				<Link to={LINKS.SIGN_UP.to}>
					<ButtonV2
						mobileFullWidth={mobileFullWidth}
						variant="secondary"
						className={
							showSignUpFreeButton
								? styles.genemodButton__v2primary_inverted
								: styles.button
						}
					>
						Sign up free
					</ButtonV2>
				</Link>
			)}
		</div>
	);
}

export type SubmitStatus = "NOT_SUBMITTED" | "SUBMITTING" | "SUCCESS" | "ERROR";

type HubspotSubmitButtonProps = ButtonProps & {
	submitStatus: SubmitStatus;
	theme: "dark" | "light";
	successMessage?: React.ReactNode;
	containerClassName?: string;
};

type HubspotSubmitButtonPropsV2 = ButtonProps & {
	submitStatus: SubmitStatus;
	successMessage?: React.ReactNode;
	containerClassName?: string;
};
export const Submitbutton = ({
	submitStatus,
	theme,
	successMessage,
	disabled,
	...props
}: HubspotSubmitButtonProps) => (
	<div className={styles.HubspotSubmitButton}>
		{submitStatus !== "SUCCESS" && (
			<Button
				{...props}
				style={{
					width: "100%",
					opacity:
						disabled || submitStatus === "SUBMITTING" ? 0.6 : 1,
				}}
				disabled={disabled || submitStatus === "SUBMITTING"}
			/>
		)}
		{submitStatus === "SUCCESS" && (
			<>
				{successMessage}
				{!successMessage && (
					<div className={styles.message}>
						{theme === "dark" && (
							<StaticImage
								loading="eager"
								src={"../../assets/icons/checkmark.svg"}
								alt="checkmark icon"
							/>
						)}
						{theme === "light" && (
							<StaticImage
								loading="eager"
								src={"../../assets/icons/checkmark_dark.svg"}
								alt="checkmark icon"
							/>
						)}
						<Typography
							variant="SUBHEADERDESC"
							color={theme === "dark" ? "ui-01" : "text-dark"}
						>
							{"Thanks, we’ll be in touch!"}
						</Typography>
					</div>
				)}
			</>
		)}
		{submitStatus === "ERROR" && (
			<Typography variant="CAPTION" color="medium-pink">
				*Failed to submit your request. Please try again or contact us
				at support@genemod.net
			</Typography>
		)}
	</div>
);

export const SubmitbuttonV2 = ({
	submitStatus,
	successMessage,
	disabled,
	containerClassName,
	...props
}: HubspotSubmitButtonPropsV2) => (
	<div className={cn(styles.HubspotSubmitButton, containerClassName)}>
		{submitStatus !== "SUCCESS" && (
			<Button
				{...props}
				style={{
					width: "100%",
					opacity:
						disabled || submitStatus === "SUBMITTING" ? 0.6 : 1,
				}}
				disabled={disabled || submitStatus === "SUBMITTING"}
			/>
		)}
		{submitStatus === "SUCCESS" && (
			<>
				{successMessage}
				{!successMessage && (
					<div className={styles.message}>
						<StaticImage
							loading="eager"
							src={"../../assets/icons/check-circle-broken.svg"}
							alt="checkmark icon"
							className={styles.successIcon}
						/>
						<TypographyV2
							color={"success-400"}
							className={styles.successMessage}
						>
							{"Thank you. We will reach out shortly."}
						</TypographyV2>
					</div>
				)}
			</>
		)}
		{submitStatus === "ERROR" && (
			<TypographyV2
				variant="BODY_TEXT_LARGE"
				weight="SEMIBOLD"
				color="medium-pink"
			>
				*Failed to submit your request. Please try again or contact us
				at support@genemod.net
			</TypographyV2>
		)}
	</div>
);
